import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, signal } from '@angular/core';
import { ReactiveFormsModule, FormBuilder, Validators, AbstractControl, ValidationErrors } from '@angular/forms';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { AuthSettingsService } from '../auth-settings.service';
import {
  AccordionComponent,
  AccordionItemComponent,
  EntityDetailLayoutComponent,
  ErrorStateComponent,
  FormField,
  HelpDialogService,
  Input,
  LoadingStateComponent,
  NumberInput,
  PageActionsDirective,
  ProfileSummaryCardComponent,
  ToastService,
  TooltipDirective,
  ZagButton,
  ZagIcon,
  ZagSwitch,
  type ProfileStat,
} from '@zag/ui';
import { type HelpTopicId } from '@zag/ai/models';
import { rxResource, takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { map, catchError, of } from 'rxjs';

interface MaskedAuthConfig {
  secret?: string;
  emailAndPassword?: {
    enabled?: boolean;
    credentialIdentifier?: 'email' | 'username';
    minUsernameLength?: number;
    maxUsernameLength?: number;
    requireEmailVerification?: boolean;
    minPasswordLength?: number;
    maxPasswordLength?: number;
  };
  socialProviders?: Record<
    string,
    {
      clientId?: string;
      clientSecret?: string;
      tenantId?: string;
    } | null
  >;
}

type ProviderStatus = 'empty' | 'partial' | 'complete';

/**
 * Cross-field validator: if any field in a group is filled, all must be filled.
 *
 * @param fields          Form control names that must all be filled or all be empty.
 * @param secretField     Name of the password/secret field (treated as "filled" when
 *                        the server already has one stored, even if the input is blank).
 * @param hasSavedSecret  Getter that returns true when the server has an existing secret.
 *
 * @internal Exported for unit testing only.
 */
export function allOrNoneValidator(
  fields: string[],
  secretField?: string,
  hasSavedSecret?: () => boolean
): (group: AbstractControl) => ValidationErrors | null {
  return (group: AbstractControl): ValidationErrors | null => {
    const rawValues = fields.map(f => ({
      field: f,
      raw: (group.get(f)?.value as string | null | undefined)?.trim() ?? '',
    }));

    // Only treat the secret as "filled from server" when at least one other field
    // is non-empty — i.e. the user is editing an existing provider, not clearing it.
    const nonSecretFieldsFilled = rawValues.filter(({ field }) => field !== secretField).some(({ raw }) => raw.length > 0);

    const values = rawValues.map(({ field, raw }) => {
      if (field === secretField && !raw && hasSavedSecret?.() && nonSecretFieldsFilled) return '●';
      return raw;
    });

    const filled = values.filter(v => v.length > 0).length;
    if (filled === 0 || filled === fields.length) return null;
    return { providerPartial: true };
  };
}

@Component({
  selector: 'sc-auth-settings',
  imports: [
    ReactiveFormsModule,
    TranslatePipe,
    EntityDetailLayoutComponent,
    ProfileSummaryCardComponent,
    AccordionComponent,
    AccordionItemComponent,
    FormField,
    Input,
    ZagSwitch,
    NumberInput,
    ZagButton,
    ZagIcon,
    TooltipDirective,
    LoadingStateComponent,
    ErrorStateComponent,
    PageActionsDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './auth-settings.component.html',
  styleUrl: './auth-settings.component.scss',
})
export class AuthSettingsComponent {
  private readonly fb = inject(FormBuilder);
  private readonly authSettings = inject(AuthSettingsService);
  private readonly toast = inject(ToastService);
  private readonly translate = inject(TranslateService);
  private readonly helpDialog = inject(HelpDialogService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly currentLang = toSignal(this.translate.onLangChange, {
    initialValue: { lang: this.translate.currentLang, translations: {} },
  });

  protected readonly isSaving = signal(false);

  protected readonly profileName = computed(() => {
    this.currentLang();
    return this.translate.instant('settings.auth.title');
  });
  protected readonly profileSubtitle = computed(() => {
    this.currentLang();
    return this.translate.instant('settings.auth.description');
  });

  protected readonly settingsForm = this.fb.group({
    // Email & Password
    emailAndPasswordEnabled: [true],
    usernameEnabled: [false],
    minPasswordLength: [8 as number | null, [Validators.min(6), Validators.max(128)]],
    maxPasswordLength: [128 as number | null, [Validators.min(8), Validators.max(256)]],
    requireEmailVerification: [false],
    // Social providers — no allowedDomains
    microsoftTenantId: [''],
    microsoftClientId: [''],
    microsoftClientSecret: [''],
    googleClientId: [''],
    googleClientSecret: [''],
    appleClientId: [''],
    appleClientSecret: [''],
    linkedinClientId: [''],
    linkedinClientSecret: [''],
  });

  // Attach cross-field group validators so the form is invalid when partially filled.
  // The hasSavedSecret callbacks are evaluated at validation time, so they correctly
  // reflect the state set by populateForm() after the config is loaded.
  constructor() {
    this.settingsForm.addValidators([
      allOrNoneValidator(
        ['microsoftTenantId', 'microsoftClientId', 'microsoftClientSecret'],
        'microsoftClientSecret',
        () => this.savedSecrets.microsoft
      ),
      allOrNoneValidator(['googleClientId', 'googleClientSecret'], 'googleClientSecret', () => this.savedSecrets.google),
      allOrNoneValidator(['appleClientId', 'appleClientSecret'], 'appleClientSecret', () => this.savedSecrets.apple),
      allOrNoneValidator(['linkedinClientId', 'linkedinClientSecret'], 'linkedinClientSecret', () => this.savedSecrets.linkedin),
    ]);
  }

  // Use toSignal so computed signals react to form value changes
  private readonly formValue = toSignal(this.settingsForm.valueChanges, {
    initialValue: this.settingsForm.value,
  });

  private providerStatus(fields: string[]): ProviderStatus {
    const values = fields.map(
      f => (this.formValue()[f as keyof typeof this.settingsForm.value] as string | null | undefined)?.trim() ?? ''
    );
    const filled = values.filter(v => v.length > 0).length;
    if (filled === 0) return 'empty';
    if (filled === fields.length) return 'complete';
    return 'partial';
  }

  protected readonly microsoftStatus = computed<ProviderStatus>(() =>
    this.providerStatus(['microsoftTenantId', 'microsoftClientId', 'microsoftClientSecret'])
  );

  protected readonly googleStatus = computed<ProviderStatus>(() => this.providerStatus(['googleClientId', 'googleClientSecret']));

  protected readonly appleStatus = computed<ProviderStatus>(() => this.providerStatus(['appleClientId', 'appleClientSecret']));

  protected readonly linkedinStatus = computed<ProviderStatus>(() => this.providerStatus(['linkedinClientId', 'linkedinClientSecret']));

  protected readonly microsoftStatusIcon = computed(() => statusToIcon(this.microsoftStatus()));
  protected readonly googleStatusIcon = computed(() => statusToIcon(this.googleStatus()));
  protected readonly appleStatusIcon = computed(() => statusToIcon(this.appleStatus()));
  protected readonly linkedinStatusIcon = computed(() => statusToIcon(this.linkedinStatus()));

  /** Returns ●●●●●● when a secret is already saved on the server, empty otherwise. */
  protected secretPlaceholder(hasSaved: boolean): string {
    return hasSaved ? '••••••••' : '';
  }

  protected openProviderHelp(provider: 'microsoft' | 'google' | 'apple' | 'linkedin'): void {
    this.helpDialog.openTopic(`socialLogin.${provider}` as HelpTopicId);
  }

  protected readonly profileStats = computed<ProfileStat[]>(() => {
    this.currentLang();
    const configured = [this.microsoftStatus(), this.googleStatus(), this.appleStatus(), this.linkedinStatus()].filter(
      s => s === 'complete'
    ).length;

    return [
      {
        label: this.translate.instant('settings.auth.stats.providers'),
        value: String(configured),
        icon: 'key',
      },
    ];
  });

  protected readonly configResource = rxResource<MaskedAuthConfig | null, void>({
    stream: () =>
      this.authSettings.getConfig<MaskedAuthConfig>().pipe(
        map(response => {
          this.populateForm(response);
          return response;
        }),
        catchError(() => {
          this.toast.error(this.translate.instant('settings.auth.loadError'));
          return of(null);
        })
      ),
  });

  // Tracks whether each provider has a client secret already saved on the server.
  // The server returns null for clientSecret and sets hasClientSecret: boolean.
  protected readonly savedSecrets = {
    microsoft: false,
    google: false,
    apple: false,
    linkedin: false,
  };

  private populateForm(config: MaskedAuthConfig & { socialProviders?: Record<string, { hasClientSecret?: boolean } | null> }): void {
    const ep = config.emailAndPassword ?? {};
    const sp = config.socialProviders ?? {};

    // Track which providers have a secret stored on the server
    this.savedSecrets.microsoft = !!(sp['microsoft'] as { hasClientSecret?: boolean } | null)?.hasClientSecret;
    this.savedSecrets.google = !!(sp['google'] as { hasClientSecret?: boolean } | null)?.hasClientSecret;
    this.savedSecrets.apple = !!(sp['apple'] as { hasClientSecret?: boolean } | null)?.hasClientSecret;
    this.savedSecrets.linkedin = !!(sp['linkedin'] as { hasClientSecret?: boolean } | null)?.hasClientSecret;

    // Secret fields are always left blank — typing a new value replaces it,
    // leaving blank preserves the existing secret (backend detects null/empty = keep).
    this.settingsForm.patchValue({
      emailAndPasswordEnabled: ep.enabled ?? true,
      usernameEnabled: ep.credentialIdentifier === 'username',
      minPasswordLength: ep.minPasswordLength ?? 8,
      maxPasswordLength: ep.maxPasswordLength ?? 128,
      requireEmailVerification: ep.requireEmailVerification ?? false,
      microsoftTenantId: sp['microsoft']?.tenantId ?? '',
      microsoftClientId: sp['microsoft']?.clientId ?? '',
      microsoftClientSecret: '',
      googleClientId: sp['google']?.clientId ?? '',
      googleClientSecret: '',
      appleClientId: sp['apple']?.clientId ?? '',
      appleClientSecret: '',
      linkedinClientId: sp['linkedin']?.clientId ?? '',
      linkedinClientSecret: '',
    });
  }

  protected onSave(): void {
    if (!this.settingsForm.valid) return;
    this.isSaving.set(true);

    const v = this.settingsForm.value;

    /**
     * Build a single provider's payload.
     * - All credential fields empty AND no saved secret → null (remove from config)
     * - Otherwise → return the object. clientSecret is null when blank (backend preserves).
     */
    const buildProvider = (
      nonSecretFields: Record<string, string | undefined>,
      newSecret: string | undefined
    ): Record<string, string | null> | null | undefined => {
      const nonSecretValues = Object.values(nonSecretFields).map(f => f?.trim() ?? '');
      const anyNonSecretFilled = nonSecretValues.some(v => v.length > 0);
      const trimmedNew = newSecret?.trim() ?? '';

      // null means "preserve existing" on the backend
      const resolvedSecret: string | null = trimmedNew || null;

      // Remove the provider when all non-secret fields are cleared and no new secret is
      // entered — this covers the "clear provider" case regardless of a previously-saved secret.
      if (!anyNonSecretFilled && !trimmedNew) return null;

      return { ...nonSecretFields, clientSecret: resolvedSecret } as Record<string, string | null>;
    };

    const socialProviders: Record<string, Record<string, string | null> | null> = {};

    const microsoft = buildProvider(
      { tenantId: v.microsoftTenantId ?? '', clientId: v.microsoftClientId ?? '' },
      v.microsoftClientSecret ?? undefined
    );
    if (microsoft !== undefined) socialProviders['microsoft'] = microsoft;

    const google = buildProvider({ clientId: v.googleClientId ?? '' }, v.googleClientSecret ?? undefined);
    if (google !== undefined) socialProviders['google'] = google;

    const apple = buildProvider({ clientId: v.appleClientId ?? '' }, v.appleClientSecret ?? undefined);
    if (apple !== undefined) socialProviders['apple'] = apple;

    const linkedin = buildProvider({ clientId: v.linkedinClientId ?? '' }, v.linkedinClientSecret ?? undefined);
    if (linkedin !== undefined) socialProviders['linkedin'] = linkedin;

    const hasSocial = Object.values(socialProviders).some(p => p !== null);
    const hasEmailPassword = !!v.emailAndPasswordEnabled;
    const authMode: string[] = [];
    if (hasEmailPassword) authMode.push('emailPassword');
    if (hasSocial) authMode.push('social');

    const payload = {
      authMode,
      emailAndPassword: {
        enabled: v.emailAndPasswordEnabled,
        credentialIdentifier: v.usernameEnabled ? 'username' : 'email',
        minPasswordLength: v.minPasswordLength,
        maxPasswordLength: v.maxPasswordLength,
        requireEmailVerification: v.requireEmailVerification,
      },
      socialProviders,
    };

    this.authSettings
      .patchConfig<MaskedAuthConfig>(payload)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.isSaving.set(false);
          this.toast.success(this.translate.instant('settings.auth.saveSuccess'));
          this.configResource.reload();
        },
        error: () => {
          this.isSaving.set(false);
          this.toast.error(this.translate.instant('settings.auth.saveError'));
        },
      });
  }
}

function statusToIcon(status: ProviderStatus): string | null {
  if (status === 'complete') return 'check_circle';
  if (status === 'partial') return 'warning';
  return null;
}
